/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useCallback, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with utc plugin
dayjs.extend(utc);
dayjs.extend(timezone);

// components imports
import { Form, DatePicker, Card, Flex } from "antd";
import { AnalyticsCharts } from "../AnalyticsCharts";

// misc imports
import {
  DEFAULT_DATETIME_FORMAT,
  HOURLY_ANALYTICS_THRESHOLD,
} from "@/constants";
import { useGetAnalyticsWithParams } from "@/api/projects";
import { useUserInfo } from "@/api/user";
import { AnalyticsTable } from "../AnalyticsTable/AnalyticsTable";
import { tokens as Tokens } from "@/utils/tokens";
import { queryClient } from "@/utils/queryClient";
import { ITokens } from "@/interfaces/User.interface";
import { InfoCard } from "../../InfoCard";
import { ITotalHits } from "@/interfaces/Analytics.interface";

const { RangePicker } = DatePicker;

/**
 * Project Analytics component
 * @returns {JSX.Element}
 */
export const ProjectAnalytics = (): JSX.Element => {
  const { workspaceId, projectId } = useParams();

  const params = useMemo(() => {
    return {
      workspaceId: workspaceId as string,
      projectId: projectId as string,
    };
  }, [workspaceId, projectId]);

  const tokensInstance = Tokens;
  const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken } = userAuth || {};
  const { data: userInfo } = useUserInfo(
    tokensInstance.getTokenSub(accessToken) as string
  );
  const { t } = useTranslation();
  const [filtersForm] = Form.useForm();
  const [, updateState] = useState(); // Hook to force a component re-render
  const [lineChartParams, setLineChartParams] = useState({
    ...params,
    queryParams: {},
  });
  const rangePickerField = Form.useWatch("rangePicker", filtersForm);
  const { data: analyticsData, isLoading: isLoadingAnalytics } =
    useGetAnalyticsWithParams(lineChartParams);

  const getSavedTokens = () => {
    // Find the total hits object, if it doesn't exist return
    const totalHits = analyticsData?.find((item) => item.id === "total_hits");
    if (!totalHits) {
      return;
    }
    const hitsData = totalHits.data as ITotalHits[];
    // We sum the saved tokens for each entry in the date range
    return hitsData.reduce(
      (acc, entry) => {
        acc.completionTokens += entry.saved_completion_tokens;
        acc.promptTokens += entry.saved_prompt_tokens;
        return acc;
      },
      { completionTokens: 0, promptTokens: 0 }
    );
  };

  const getAvgLatency = () => {
    // Find the total hits object, if it doesn't exist return
    const totalHits = analyticsData?.find((item) => item.id === "total_hits");
    if (!totalHits) {
      return;
    }
    // We want to average over the entries that have cache hits
    const hitsData = (totalHits.data as ITotalHits[]).filter(
      (entry) => entry.total_hits > 0
    );
    if (!hitsData.length) {
      return { withCache: -1 };
    }
    // We sum the entries' saved time and cache hits
    const dataSums = hitsData.reduce(
      (acc, entry) => {
        acc.savedTime += entry.saved_total_time;
        acc.hits += entry.total_hits;
        return acc;
      },
      { savedTime: 0, hits: 0 }
    );
    // We divide the two sums to get the avg value
    return {
      withCache: Math.floor(dataSums.savedTime / dataSums.hits),
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totalSavedTokens = useMemo(() => getSavedTokens(), [analyticsData]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const avgLatency = useMemo(() => getAvgLatency(), [analyticsData]);

  useEffect(() => {
    if (!rangePickerField) {
      return;
    }
    setLineChartParams({
      ...params,
      queryParams: {
        total_hits_start_date: rangePickerField[0]
          ?.tz(userInfo?.timezone, true)
          .format(),
        total_hits_end_date: rangePickerField[1]
          ?.tz(userInfo?.timezone, true)
          .format(),
        qna_history_start_date: rangePickerField[0]
          ?.tz(userInfo?.timezone, true)
          .format(),
        qna_history_end_date: rangePickerField[1]
          ?.tz(userInfo?.timezone, true)
          .format(),
      },
    });
  }, [rangePickerField, filtersForm, params, userInfo?.timezone]);

  const rangeIsHourly = useCallback(() => {
    if (!rangePickerField) {
      return false;
    }
    const day1 = dayjs(rangePickerField[0]);
    const day2 = dayjs(rangePickerField[1]);
    if (day2.diff(day1, "day") <= HOURLY_ANALYTICS_THRESHOLD) {
      return true;
    }
    return false;
  }, [rangePickerField]);

  return (
    <Card>
      <Flex align="flex-start" gap={12} wrap="wrap" className="mb-5">
        <InfoCard
          title={t("analytics.tokens_saved")}
          tooltip={t("analytics.tokens_tooltip")}
          height={"6.25rem"}
          loading={isLoadingAnalytics}
          info={[
            {
              label: t("analytics.tokens_saved_completion"),
              value: totalSavedTokens?.completionTokens.toString() ?? "0",
            },
            {
              label: t("analytics.tokens_saved_prompt"),
              value: totalSavedTokens?.promptTokens.toString() ?? "0",
            },
          ]}
          colorBg="#6b7395"
          color="#626a88"
        />
        <InfoCard
          title={t("analytics.latency")}
          tooltip={t("analytics.latency_tooltip")}
          height={"6.25rem"}
          loading={isLoadingAnalytics}
          info={[
            {
              label: t("analytics.avg_latency_decrease"),
              value:
                avgLatency && avgLatency.withCache > -1
                  ? avgLatency.withCache.toString() + "ms"
                  : "n/a",
            },
          ]}
          colorBg="#4b4cb7"
          color="#363689"
        />
        {/* Spacer element */}
        <div className="flex-fill"></div>
        <Form
          layout="vertical"
          name="filtersForm"
          form={filtersForm}
          initialValues={{
            rangePicker: [
              dayjs().subtract(1, "month").startOf("day"),
              dayjs().endOf("day"),
            ],
          }}
          onValuesChange={(_, allFields) => {
            updateState(allFields);
          }}
        >
          <Form.Item className="mb-1" name="rangePicker">
            <RangePicker
              format={DEFAULT_DATETIME_FORMAT}
              size="large"
              allowClear={true}
              onChange={(dates) => {
                if (dates === null) {
                  filtersForm.setFieldsValue({
                    rangePicker: [
                      dayjs().subtract(1, "month").startOf("day"),
                      dayjs().endOf("day"),
                    ],
                  });
                } else {
                  filtersForm.setFieldsValue({
                    rangePicker: [
                      dates[0]?.startOf("day"),
                      dates[1]?.endOf("day"),
                    ],
                  });
                }
              }}
            />
          </Form.Item>

          <p style={{ fontSize: "0.8rem" }}>
            {t("analytics.timezone")}: {userInfo?.timezone}
          </p>
        </Form>
      </Flex>

      <AnalyticsCharts
        analyticsData={analyticsData}
        userTimezone={userInfo?.timezone}
        rangeToBeHourly={rangeIsHourly()}
      />
      <AnalyticsTable
        analyticsData={analyticsData}
        userTimezone={userInfo?.timezone}
      />
    </Card>
  );
};
